import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/inspire.css';
import '../styles/inspire-home.css';
import '../styles/service-page.css';

const StepUpScholarship = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const goToContact = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  const focusItems = [
    'Emotional regulation and coping skills',
    'School-related anxiety',
    'ADHD and executive functioning challenges',
    'The emotional impact of learning differences such as dyslexia and autism',
    'Confidence and self-advocacy'
  ];

  return (
    <div className="therapy-site" data-testid="step-up-scholarship-page">
      <Helmet>
        <title>Step Up For Students Counseling | Inspire Therapy Collective | Lake Nona</title>
        <meta name="description" content="Inspire Therapy Collective is an approved provider on the Step Up For Students EMA Marketplace. We bill your child's scholarship directly for counseling. Lake Nona, Orlando & telehealth statewide." />
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="service-hero">
        <div className="service-hero-gradient" />
        <div className="service-hero-content fade-in-up">
          <h1 className="gradient-text" data-testid="step-up-page-title">Use Your Step Up For Students Scholarship for Counseling</h1>
          <span className="service-location-tag">Lake Nona, Orlando FL &bull; Telehealth across Florida</span>
        </div>
      </section>

      {/* Intro */}
      <section className="service-intro">
        <div className="container">
          <div className="service-intro-content fade-in-up">
            <p>
              Inspire Therapy Collective is an approved provider on the Step Up For Students EMA Marketplace. We work with families using Step Up scholarship funds across all programs, and those funds can be used for counseling sessions, including telehealth.
            </p>
            <p>
              Because we're on the EMA Marketplace, we can bill your Step Up scholarship directly, which means no paying up front and waiting on reimbursement. Find Inspire Therapy Collective in the EMA Marketplace, or <a href="/" onClick={(e) => { e.preventDefault(); goToContact(); }} style={{ color: 'var(--rose-accent)', textDecoration: 'underline', cursor: 'pointer' }}>reach out</a> and we'll walk you through it.
            </p>
            <p>
              School struggles rarely stay at school. Worry before the bus, meltdowns over homework, a diagnosis that leaves a child feeling different: these show up at home and in the classroom. Counseling helps children and teens build confidence and the emotional and practical skills that support them in both places.
            </p>
          </div>

          <div className="violet-badge-inline fade-in-up" data-testid="step-up-badge-page">
            <a href="https://www.stepupforstudents.org" target="_blank" rel="noopener noreferrer" className="violet-badge-link">
              <img 
                src="https://customer-assets-7cd3h4nn.emergentagent.net/job_50e26051-612f-4752-a7df-90e8e02588f2/artifacts/pxsfg4tg_IMG_9986.jpeg" 
                alt="Step Up For Students logo" 
                className="violet-badge-img"
              />
            </a>
            <p className="body-small violet-badge-text">Inspire Therapy Collective is an approved Step Up For Students EMA Marketplace provider.</p>
          </div>
        </div>
      </section>

      {/* Counseling may focus on */}
      <section className="service-help-section">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text" data-testid="step-up-focus-heading">Counseling may focus on</h2>
          <div className="service-help-grid">
            {focusItems.map((item, index) => (
              <div key={index} className="service-help-item fade-in-up" style={{ animationDelay: `${index * 0.05}s` }}>
                <div className="service-help-icon">
                  <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id={`stepUpIcon${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#F9C8A8" />
                        <stop offset="50%" stopColor="#E6A9C0" />
                        <stop offset="100%" stopColor="#C4A8E2" />
                      </linearGradient>
                    </defs>
                    <circle cx="14" cy="14" r="10" stroke={`url(#stepUpIcon${index})`} strokeWidth="1.5" fill="none" opacity="0.3" />
                    <path d="M10 14L13 17L18 11" stroke={`url(#stepUpIcon${index})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <p>{item}</p>
              </div>
            ))}
          </div>

          <div className="service-approach-content fade-in-up" style={{ marginTop: 'var(--spacing-xl)' }}>
            <p>
              Parents are essential partners. We collaborate closely with families so the strategies built in session carry into home and school.
            </p>
            <p>
              As a Licensed Clinical Social Worker with extensive experience working with children, families, and school systems, I bring a rare understanding of both a child's emotional needs and the demands of the educational environment.
            </p>
            <p>
              My goal is to identify your child's strengths, understand their areas of need, and develop practical strategies that work at home and at school.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="service-approach">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text" data-testid="step-up-how-heading">How it works</h2>
          <svg className="service-sun-divider fade-in" viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="sunDividerStepUp" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#F9C8A8" />
                <stop offset="50%" stopColor="#E6A9C0" />
                <stop offset="100%" stopColor="#C4A8E2" />
              </linearGradient>
            </defs>
            <line x1="10" y1="45" x2="70" y2="45" stroke="url(#sunDividerStepUp)" strokeWidth="1.5" opacity="0.35" />
            <path d="M 40 45 A 15 15 0 0 1 25 45 L 40 45 Z" fill="url(#sunDividerStepUp)" opacity="0.75" />
          </svg>
          <div className="service-approach-content fade-in-up">
            <p>
              We bill Step Up For Students directly through EMA, so there's no out-of-pocket cost when scholarship funds are available. You simply approve each session in your EMA account. Search for Inspire Therapy Collective under Find Providers in the EMA Marketplace, or contact us and we'll walk you through it.
            </p>
          </div>
          <div className="fade-in-up" style={{ textAlign: 'center', marginTop: 'var(--spacing-lg)' }}>
            <button onClick={goToContact} className="btn btn-primary" data-testid="step-up-get-in-touch">
              Get In Touch
            </button>
          </div>
        </div>
      </section>

      {/* Please note */}
      <section className="service-intro">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text" data-testid="step-up-note-heading">Please note</h2>
          <div className="step-up-note-box fade-in-up" data-testid="step-up-note-box">
            <p>
              Step Up For Students determines eligibility and funding for each scholarship program, and we're happy to help you confirm coverage before your first session.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="service-cta">
        <div className="container">
          <div className="service-cta-content fade-in-up">
            <div className="service-cta-details">
              <a href="tel:4072019895" className="cta-phone">(407) 201-9895</a>
              <span className="cta-location">Lake Nona, Orlando FL &bull; Telehealth across Florida</span>
            </div>
            <button onClick={() => navigate('/', { state: { scrollTo: 'services' } })} className="explore-all-link" data-testid="step-up-explore-all">
              &larr; Explore All Services
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StepUpScholarship;
